import { ReactNode, useState } from 'react'
import { Button, Space, Image, Spin, Tooltip } from '@arco-design/web-react'
import NextImage from 'next/image'
import IconClock from '@/assets/clock.svg'
import IconHeart from '@/assets/heart.svg'
import IconMessage from '@/assets/message.svg'
import IconPlay from '@/assets/play-circle.svg'
import IconShare from '@/assets/share.svg'
import VideoIcon from '@/assets/video_play.svg'

import IconHeartLight from '@/assets/ic_heart_light.svg'
import IconMessageLight from '@/assets/ic_message_light.svg'
import IconPlayLight from '@/assets/ic_play_light.svg'
import IconShareLight from '@/assets/ic_share_light.svg'
import IconVideoLarge from '@/assets/video_large.png'
import IconVideoLarge2 from '@/assets/video_large_2.png'
import IconLiveLarge from '@/assets/live-large.png'
import IconUser from '@/assets/user2x.png'
import IconProduct from '@/assets/shop2x.png'

import IconEye from '@/assets/eye.svg'
import IconRanking from '@/assets/ranking.svg'
import IconStatusUp from '@/assets/status-up.svg'
import IconWalletMoney from '@/assets/wallet-money.svg'

import Mask from '../Mask'
import styles from './index.module.less'
import { useRouter } from 'next/router'
import { VideoModal } from '@/components/VideoModal'
import { getFillCoverUrl } from '@/utils'
import { IconPlayArrow, IconSearch } from '@arco-design/web-react/icon'
import useLocale from '@/utils/useLocale'
import { isBlurValue, isEmpty } from '@/utils/is'
import EchoNextImage from '@/components/EchoNextImage'

interface ILiveDetail {
  live_id: string
  title: string
  reflow_cover: string
  create_time: string
  fished_time: string
  total_views_cnt: string
  max_views_cnt: string
  duration: string
  total_sale_cnt: string
  total_gmv_amt: string
  total_product_cnt: string
}

export interface IVideoInfluencer {
  avatar_url: string
  follower_count: string
  region: string
  unique_id: string
}

export interface IVideoDetail {
  duration: string
  play_count: string
  digg_count: string
  comment_count: string
  share_count: string
  influencer: IVideoInfluencer
  views_count: string
  likes_count: string
  comments_count: string
  shares_count: string
  video_title: string
  video_title_brief: string
  cover_url: string
  create_time?: string
  avatar?: string
  nick_name?: string
  video_url?: string
  video_id?: string
  share_url?: string
  product?: {
    product_id?: string
    product_name?: string
    product_name_brief?: string
    cover_url?: string
    total_sale_cnt?: string
    total_gmv_amt?: string
    video_sale_cnt?: string
    video_gmv_amt?: string
  }
}

type StatisticItemType = {
  type?: 'main' | 'normal' | 'video' | 'video-card' | 'live'
  icon?: string
  title?: ReactNode
  count?: ReactNode
  total?: ReactNode
  totalDesc?: ReactNode
  loading?: boolean
  unit?: ReactNode
  averageData?: {
    desc: string
    num: number | string
  }[]
  detail?: IVideoDetail
  liveDetail?: ILiveDetail
}

function StatisticCard(props: StatisticItemType) {
  const {
    type = 'normal',
    icon,
    title,
    count,
    total,
    totalDesc,
    loading,
    unit,
    averageData,
    detail,
    liveDetail
  } = props

  const router = useRouter()
  const [visible, setVisible] = useState(false)
  const [url, setUrl] = useState('')
  const t = useLocale()

  if (type === 'video') {
    return (
      <div className={styles['video-item']}>
        <div className={styles['video-title']}>{title}</div>
        <div className={styles['video-content']}>
          <div
            onClick={() => {
              if (detail?.share_url) {
                setUrl(detail.share_url)
                setVisible(true)
              } else {
                detail?.video_url && window.open(detail?.video_url, '_blank')
              }
            }}
            style={detail?.video_url ? { cursor: 'pointer' } : { cursor: 'auto' }}
          >
            <EchoNextImage
              src={detail?.cover_url || IconLiveLarge?.src}
              alt="video-cover"
              width={114}
              height={200}
              className={styles['video-img']}
              preview={false}
              // onError={({ currentTarget }) => {
              //   currentTarget.onerror = null // prevents looping
              //   currentTarget.src = IconLiveLarge?.src
              // }}
            />
            <div className={styles['product-video-Montmorillonite']}>
              <VideoIcon />
            </div>
          </div>
          <div className={styles['video-info']}>
            <div className={styles['video-info-item']}>
              <IconClock />
              <div>{detail?.duration}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconPlay />
              <div>{detail?.views_count}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconHeart />
              <div>{detail?.likes_count}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconMessage />
              <div>{detail?.comments_count}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconShare />
              <div>{detail?.shares_count}</div>
            </div>
          </div>
        </div>
        <Tooltip content={detail?.video_title}>
          <div
            className={styles['video-footer']}
            onClick={() => {
              detail?.video_id ? router.push('/videos/' + detail.video_id) : ''
            }}
          >
            {detail?.video_title}
          </div>
        </Tooltip>
        <VideoModal
          videoId={detail?.video_id}
          visible={visible}
          url={url}
          onCancel={() => {
            setVisible(false)
            setUrl('')
          }}
        />
      </div>
    )
  }

  if (type === 'live') {
    return (
      <div className={styles['video-item']}>
        <div className={styles['video-title']}>{title}</div>
        <div
          className={styles['video-content']}
          onClick={() => {
            liveDetail?.live_id && window.open(`/live/${liveDetail.live_id}`, '_blank')
          }}
          style={liveDetail?.live_id ? { cursor: 'pointer' } : { cursor: 'auto' }}
        >
          <Image
            src={liveDetail?.reflow_cover || IconVideoLarge?.src}
            alt="reflow-cover"
            width={114}
            height={200}
            className={styles['video-img']}
            preview={false}
            // error={IcVideo}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null // prevents looping
              currentTarget.src = IconVideoLarge?.src
            }}
          />
          <div className={styles['video-info']}>
            <div className={styles['video-info-item']}>
              <IconClock />
              <div>{liveDetail?.duration}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconEye />
              <div>{liveDetail?.total_views_cnt}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconRanking />
              <div>{liveDetail?.max_views_cnt}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconStatusUp />
              <div>{liveDetail?.total_sale_cnt}</div>
            </div>
            <div className={styles['video-info-item']}>
              <IconWalletMoney />
              <div>{liveDetail?.total_gmv_amt}</div>
            </div>
          </div>
        </div>
        <div className={styles['video-footer']}>{liveDetail?.title}</div>
      </div>
    )
  }

  if (type === 'video-card') {
    return (
      <div className={styles['video-card']}>
        {!isEmpty(detail?.product) && <div className={styles['video-card-content-back']}></div>}
        <div className={styles['video-card-content']}>
          <div style={{ position: 'absolute' }}>
            <EchoNextImage
              src={detail?.cover_url || IconVideoLarge2?.src}
              alt="video-card"
              width={200}
              height={246}
              preview={false}
              style={{ borderRadius: 8 }}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null // prevents looping
                currentTarget.src = IconVideoLarge2?.src
              }}
            />
          </div>
          <div className={styles['video-card-content-info-wrap']}>
            <div className={styles['video-card-content-info']}>
              <div className={styles['video-card-content-info-item']}>
                <IconPlayLight />
                <div className={styles['video-card-content-num']}>{detail?.play_count}</div>
              </div>
              <div className={styles['video-card-content-info-item']}>
                <IconHeartLight className={styles['video-card-content-icon']} />
                <div className={styles['video-card-content-num']}>{detail?.digg_count}</div>
              </div>
              <div className={styles['video-card-content-info-item']}>
                <IconMessageLight />
                <div className={styles['video-card-content-num']}>{detail?.comment_count}</div>
              </div>
              <div className={styles['video-card-content-info-item']}>
                <IconShareLight />
                <div className={styles['video-card-content-num']}>{detail?.share_count}</div>
              </div>
            </div>
          </div>
          {!isEmpty(detail?.product) && (
            <div className={styles['video-card-content-product-wrap']}>
              <div className={styles['video-card-content-product-img-wrap']}>
                <Image
                  src={detail?.product?.cover_url}
                  alt="video-card"
                  width={58}
                  className={styles['video-card-content-product-img']}
                  height={58}
                  preview={false}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null // prevents looping
                    currentTarget.src = IconProduct?.src
                  }}
                />
              </div>
              <div className={styles['video-card-content-product-info']}>
                <div className={styles['video-card-content-product-title']}>{detail?.product?.product_name}</div>
                <div className={styles['video-card-content-product-item']}>
                  <div>{t['influencer.detail.sales.table.Sales']}</div>
                  <div>{detail?.product?.video_sale_cnt}</div>
                </div>
                <div className={styles['video-card-content-product-item']}>
                  <div>{t['influencer.detail.sales.table.GMV']}</div>
                  <div>{detail?.product?.video_gmv_amt}</div>
                </div>
              </div>
            </div>
          )}
          <div className={styles['video-card-footer']}>
            <div className={styles['video-card-btn-wrap']}>
              <Button
                icon={<IconPlayArrow />}
                className={styles['video-card-btn']}
                onClick={() => {
                  if (detail?.share_url) {
                    setUrl(detail.share_url)
                    setVisible(true)
                  } else {
                    detail?.video_url && window.open(detail?.video_url, '_blank')
                  }
                }}
              >
                Play
              </Button>
              <Button
                icon={<IconSearch />}
                className={styles['video-card-btn']}
                onClick={() => {
                  detail?.video_id && window.open(`${window.location.origin}/videos/${detail.video_id}`, '_blank')
                }}
              >
                View Detail
              </Button>
              {detail?.product?.product_id && (
                <Button
                  icon={<IconSearch />}
                  className={styles['video-card-btn']}
                  onClick={() => {
                    window.open(`${window.location.origin}/products/${detail?.product?.product_id}`, '_blank')
                  }}
                >
                  View Product
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className={styles['video-card-author']}>
          <div className={styles['video-card-author-title']}>{detail?.video_title}</div>
          <div className={styles['video-card-author-influencer']}>
            <Image
              src={detail?.influencer?.avatar_url}
              alt="avatar"
              width={44}
              height={44}
              preview={false}
              className={styles['video-card-author-avatar']}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null // prevents looping
                currentTarget.src = IconUser?.src
              }}
            />
            <div className={styles['video-card-author-influencer-right']}>
              <div className={styles['video-card-author-name']}>{detail?.nick_name}</div>
              <div className={styles['video-card-author-time']}>{detail?.create_time}</div>
            </div>
          </div>
        </div>
        <VideoModal
          videoId={detail?.video_id}
          visible={visible}
          url={url}
          onCancel={() => {
            setVisible(false)
            setUrl('')
          }}
        />
      </div>
    )
  }

  if (type === 'main') {
    return (
      <div className={styles['main']}>
        <NextImage width={44} height={44} className={styles.icon} src={icon} alt="" />
        <div>
          <Mask isBlur={isBlurValue(count)}>
            <div className={styles.count}>
              {isBlurValue(count) ? `888` : count}
              <span className={styles.unit}>{unit}</span>
            </div>
          </Mask>
          <div className={styles.title}>{title}</div>
          <div className={styles['total']}>
            <div className={styles['total-desc']}>{totalDesc}</div>
            <div className={styles['total-num']}>{total}</div>
          </div>
        </div>
      </div>
    )
  }

  if (type === 'normal') {
    return (
      <div className={styles.item}>
        <div className={styles['header']}>
          <div className={styles['header-desc']}>{title}</div>
          <div className={styles['header-num']}>{count}</div>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {averageData?.map((item, index) => {
            if (index < averageData.length - 1) {
              return (
                <div className={styles['averages']} key={index}>
                  <div className={styles['average-wrap']}>
                    <div className={styles['average-desc']}>{item.desc}</div>
                    <div className={styles['average-num']}>{item.num}</div>
                  </div>
                </div>
              )
            } else {
              return (
                <div className={styles['average-wrap']} key={index}>
                  <div className={styles['average-desc']}>{item.desc}</div>
                  <div className={styles['average-num']}>{item.num}</div>
                </div>
              )
            }
          })}
        </Space>
      </div>
    )
  }
}

function StatisticCardWrap({ loading = false, blur = false, ...props }) {
  return (
    <Spin loading={loading} style={{ width: '100%' }}>
      <Mask isBlur={blur}>
        <StatisticCard {...props} />
      </Mask>
    </Spin>
  )
}

export default StatisticCardWrap
